<template>
    <div v-if="inline" ref="edited" contenteditable="true" :id="editorId" :class="classes"></div>
    <textarea v-else :name="editorId"  ref="edited" :class="classes"></textarea>
</template>
  
<script>
window.CKEDITOR_BASEPATH = process.env.VUE_APP_FRONTEND_URL + "/js/ckeditor4/";
const CKEDITOR_URL = window.CKEDITOR_BASEPATH + "ckeditor.js";
const EDITOR_CSS_URL = "/css/editor-styles.css";

import { loadScript } from "vue-plugin-load-script";
export default {
    name: "RichTextEditor",
    props: {
        css: [String, Array],
        html: {
            type: String,
            default: "",
        },
        inline: Boolean,
        id: {
            type: String,
            default: "rich-text-editor",
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        stopGlobalListeners: Boolean, 
    },
    components: {
    },
    data: () => ({
        notifyOptions: {
            title: 'Редактор текста',
        },
        isLoading: false,
        initialized: false,
        htmlToChange: false,
    }),
    computed: {
        currentEditor() {
            return window.CKEDITOR?.instances && window.CKEDITOR.instances[this.editorId] || null;
        },
        classes() {
            const result = this.$attrs.class;
            return ["page-editor", ...(Array.isArray(result) ? result : [result])].filter(v => !!v);
        },
        editorId() {
            return this.id + '-' + this._uid;
        },
    },
    created() {
        this.htmlToChange = true;
        if(this.isLoading || window.CKEDITOR_IS_LOADING) {
            return;
        }
        if (!window.CKEDITOR) {
            this.isLoading = true;
            window.CKEDITOR_IS_LOADING = true;
            loadScript(CKEDITOR_URL)
                .then(() => {
                    this.ckEditorLoaded();
                })
                .catch(this.notifyError)
                .finally(() => {
                    this.isLoading = false;
                    window.CKEDITOR_IS_LOADING = false;
                });
        } else {
            this.ckEditorLoaded();
        }
    },
    mounted() {
        if(window.CKEDITOR) {
            return this.startEditor();
        }
        const waitCkInterval = setInterval(() => {
            if(!window.CKEDITOR) {
                return;
            }
            clearInterval(waitCkInterval);
            this.startEditor();
        }, 1);
    },
    beforeDestroy() {
        this.initialized = false;
        window.CKEDITOR.removeListener('b64iImageLoaded', this.onImageLoaded);
        window.CKEDITOR.removeListener('instanceCreated', this.onInstanceCreated);
        this.currentEditor?.destroy();
    },
    methods: {
        startEditor() {
            if(this.initialized) {
                this.updateEditorHtml();
                return;
            }
            this.initEditor();
        },
        onImageLoaded(e) {
            this.$emit('imageLoaded', e.data);
        },
        onInstanceCreated(e) {
            const editor = e.editor;
            //const element = editor.element;
            //if (element.is('h1', 'h2', 'h3') || element.getAttribute('id') == 'taglist') {
            editor.on('configLoaded', () => {
                editor.config.startupFocus = false;
                editor.config.removePlugins = 'pdfview,pdfblock,exportpdf,htmldataprocessor';
                editor.config.removeButtons = 'Save,NewPage,DocProps,Preview,Print,Templates,document,Cut,Copy,Paste,Undo,Redo,Find,Replace,SelectAll,Scayt,Form,Checkbox,Radio,TextField,Textarea,Select,Button,ImageButton,HiddenField,BidiLtr,BidiRtl,Image,Flash,PageBreak,Iframe,button1,button2,button3,oembed,MediaEmbed,About';
                editor.config.toolbarGroups = [
                    { name: 'document', groups: [ 'source', 'mode', 'document', 'doctools' ] },
                    { name: 'clipboard', groups: [ 'clipboard', 'undo' ] },
                    { name: 'editing', groups: [ 'find', 'selection', 'spellchecker', 'editing' ] },
                    // { name: 'forms', groups: [ 'forms' ] },
                    { name: 'basicstyles', groups: [ 'basicstyles', 'cleanup' ] },
                    { name: '/', groups: ['/']},
                    { name: 'paragraph', groups: [ 'list', 'indent', 'blocks', 'align', 'paragraph' ] },
                    { name: 'links', groups: [ 'links' ] },
                    { name: 'insert', groups: [ 'insert' ] },
                    { name: 'styles', groups: [ 'styles' ] },
                    { name: 'colors', groups: [ 'colors' ] },
                    { name: 'tools', groups: [ 'tools' ] },
                    { name: 'others', groups: [ 'others' ] }
                ];
                editor.config.contentsCss = EDITOR_CSS_URL;
                if (editor.config.startupFocus) {
                    editor.focus();
                }
            });
            //}
        },
        ckEditorLoaded() {
            if(!this.inline || this.stopGlobalListeners) {
                return;
            }
            window.CKEDITOR.on('b64iImageLoaded', this.onImageLoaded);
            window.CKEDITOR.on('instanceCreated', this.onInstanceCreated);
        },
        initEditor() {
            this.initialized = true;
            window.CKEDITOR.disableAutoInline = true;
            
            const options = Object.assign({
                defaultLanguage: "ru",
                language: "ru",
                extraPlugins: ["base64image", "pastebase64", "sourcedialog", "stylesheetparser"/* 'pdfview', 'pdfblock' */],
                allowedContent: true, //'div{*}',
                contentsCss: this.css,
                startupFocus : false,
                top: {
                    width: '900px',
                },
                on : {
                    instanceReady: e  => {
                        e.editor.ui.space( 'top' ).setStyle( 'width', (this.$el.offsetWidth - 19) + 'px');
                        if (this.htmlToChange) {
                            this.updateEditorHtml();
                        }
                        e.editor.editable().$.blur();
                        this.$emit('ready');
                        this.resetScroll('auto');
                    },
                    change: e => {
                        this.$emit('update:html', e.editor.getData());
                    },
                }
            }, this.options);
            const ckInstance = window.CKEDITOR[this.inline ? 'inline' : 'replace'](this.editorId, options);
            if (!ckInstance) {
                this.notifyError(`При инициализации редактора (${this.editorId}), не обнаружен его экземпляр`);
                return;
            }
            const stopFunction = () => {
                ckInstance.removeListener('focus', stopFunction);
                return false;
            };
            ckInstance.on('focus', stopFunction);
        },
        updateEditorHtml() {
            if (!this.currentEditor) {
                console.warn('Mo currentEditor');
                return;
            }
            var sel = this.currentEditor.getSelection();
            var range = sel.getRanges()[0];
            // no range, means the editor is empty. Select the range.
            if (!range) {
                range = this.currentEditor.createRange();
                range.selectNodeContents( this.currentEditor.editable() );
                sel.selectRanges( [ range ] );
            }
            this.currentEditor.insertHtml(this.html, 'unfiltered_html', range);
            this.htmlToChange = false;
        },
        resetScroll(behavior = 'smooth', top = 0) {
            const rs = {top, behavior};
            this.$refs.edited.scroll(rs);
            document.body.scroll(rs);
            document.documentElement.scroll(rs);
        },
    }
};
</script>
<style lang="scss" scoped>
    .cke_editor_rich-text-editor.cke_reset {
       margin-left: -1px;
    }
    .page-editor {
        &.overflow-hidden {
            overflow: hidden;
        }
        &.max-width {
            width: 100%;
        }
        &.max-height {
            // height: 100%;
        }
    }

</style>
  